import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useChatStore } from '../store/chat';
import { useWorkflowStore } from '../store/workflow';

// Icon Components
function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  );
}

function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function PuzzleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.96.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export function DashboardPage() {
  const { conversations, isLoadingConversations, fetchConversations } = useChatStore();
  const { workflows, isLoading: isLoadingWorkflows, fetchWorkflows } = useWorkflowStore();

  const isLoading = isLoadingConversations || isLoadingWorkflows;

  // Load data on mount (cache-aware)
  useEffect(() => {
    fetchConversations();
    fetchWorkflows();
  }, []);

  const recentConversations = conversations.slice(0, 5);
  const recentWorkflows = workflows.slice(0, 5);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-full bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-gray-500">Welcome to n8n Builder</p>
      </header>

      {/* Stats Cards */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* Conversations Card */}
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50">
              <ChatBubbleIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Conversations</p>
              {isLoading ? (
                <div className="mt-1 h-7 w-12 rounded skeleton" />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{conversations.length}</p>
              )}
            </div>
          </div>
        </div>

        {/* Workflows Card */}
        <div className="card p-5">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-50">
              <CubeIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Workflows</p>
              {isLoading ? (
                <div className="mt-1 h-7 w-12 rounded skeleton" />
              ) : (
                <p className="text-2xl font-bold text-gray-900">{workflows.length}</p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Start Card */}
        <div className="card p-5 sm:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Quick Start</p>
              <p className="mt-1 text-sm text-gray-600">Create your next automation</p>
            </div>
            <Link
              to="/workflow-builder"
              className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4" />
              New Workflow
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Quick Actions</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Link
            to="/workflow-builder"
            className="card card-hover flex items-center gap-4 p-5 transition-all duration-200 hover:-translate-y-0.5"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50">
              <ChatBubbleIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Workflow Builder</h3>
              <p className="mt-0.5 text-sm text-gray-500">Create workflows with AI</p>
            </div>
            <ArrowRightIcon className="h-5 w-5 text-gray-400" />
          </Link>

          <Link
            to="/connect"
            className="card card-hover flex items-center gap-4 p-5 transition-all duration-200 hover:-translate-y-0.5"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-purple-50">
              <PuzzleIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">MCP Setup</h3>
              <p className="mt-0.5 text-sm text-gray-500">Configure API keys & instances</p>
            </div>
            <ArrowRightIcon className="h-5 w-5 text-gray-400" />
          </Link>

          <Link
            to="/workflows"
            className="card card-hover flex items-center gap-4 p-5 transition-all duration-200 hover:-translate-y-0.5"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-green-50">
              <CubeIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">My Workflows</h3>
              <p className="mt-0.5 text-sm text-gray-500">View saved workflows</p>
            </div>
            <ArrowRightIcon className="h-5 w-5 text-gray-400" />
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Conversations */}
        <div className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Conversations</h2>
            <Link
              to="/workflow-builder"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg skeleton" />
              ))}
            </div>
          ) : recentConversations.length === 0 ? (
            <div className="py-8 text-center">
              <ChatBubbleIcon className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No conversations yet</p>
              <Link
                to="/workflow-builder"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="h-4 w-4" />
                Start a conversation
              </Link>
            </div>
          ) : (
            <ul className="space-y-2">
              {recentConversations.map((conv) => (
                <li key={conv.id}>
                  <Link
                    to="/workflow-builder"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-gray-50"
                  >
                    <ChatBubbleIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    <span className="flex-1 truncate font-medium text-gray-700">{conv.title}</span>
                    <span className="flex items-center gap-1 text-xs text-gray-400">
                      <ClockIcon className="h-3 w-3" />
                      {formatDate(conv.updatedAt)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent Workflows */}
        <div className="card p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Workflows</h2>
            <Link
              to="/workflows"
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              View all
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 rounded-lg skeleton" />
              ))}
            </div>
          ) : recentWorkflows.length === 0 ? (
            <div className="py-8 text-center">
              <CubeIcon className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">No workflows yet</p>
              <Link
                to="/workflow-builder"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                <PlusIcon className="h-4 w-4" />
                Create a workflow
              </Link>
            </div>
          ) : (
            <ul className="space-y-2">
              {recentWorkflows.map((workflow) => (
                <li key={workflow.id}>
                  <Link
                    to="/workflows"
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-gray-50"
                  >
                    <CubeIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    <span className="flex-1 truncate font-medium text-gray-700">{workflow.name}</span>
                    <span className="text-xs text-gray-400">{workflow.nodeCount} nodes</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
